import { PRIVATE_MESSAGE_EVENTS, TRADE_EVENTS } from '@moal/api'
import htmlTruncate from 'html-truncate'
import PropTypes from 'prop-types'
import { useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { MEMBERS, MODERATORS, TRADES } from '../../../constants/chatRooms'
import * as FEATURES from '../../../constants/features'
import * as SETTINGS from '../../../constants/settings'
import { AuthenticationContext } from '../../../context/authenticationContext'
import { SiteConfigContext } from '../../../context/siteConfigContext'
import { SocketContext } from '../../../context/socketContext'
import { putUser } from '../../../utils/APIs/users'
import { PRODUCT_CODE } from '../../../utils/ENV'
import { hasStaffPrivilege } from '../../../utils/RequireRole'
import {
  setPageTitleCounter,
  setPageTitleDefault
} from '../../../utils/utilities'

const ChatSectionNav = ({ active, handleSetChannel }) => {
  const { [SETTINGS.AUDIO.key]: audio } = useContext(SiteConfigContext)
  const { user, updateUser } = useContext(AuthenticationContext)
  const { privateMessagesSocket, tradesSocket } = useContext(SocketContext)
  const [modNotifCounter, setModNotifCounter] = useState(
    (user && user.userService?.notifications?.privateMessages) || 0
  )
  const [tradeNotifCounter, setTradeNotifCounter] = useState(0)

  const { [SETTINGS.FEATURES.key]: enabledFeatures } =
    useContext(SiteConfigContext)
  const tradesEnabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.TRADES],
    [enabledFeatures]
  )
  const publicChatEnabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.PUBLIC_CHAT],
    [enabledFeatures]
  )
  const privateChatEnabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.PRIVATE_CHAT],
    [enabledFeatures]
  )

  useEffect(() => {
    if (tradesSocket) {
      tradesSocket.on(TRADE_EVENTS.POST, handlePostTrade)
    }
    if (privateMessagesSocket) {
      privateMessagesSocket.on(
        PRIVATE_MESSAGE_EVENTS.POST,
        handleNewPrivateMessage
      )
    }
    return () => {
      if (tradesSocket) {
        tradesSocket.off(TRADE_EVENTS.POST, handlePostTrade)
      }
      if (privateMessagesSocket) {
        privateMessagesSocket.off(
          PRIVATE_MESSAGE_EVENTS.POST,
          handleNewPrivateMessage
        )
      }
    }
  }, [tradesSocket, privateMessagesSocket, active])

  const handleNewPrivateMessage = (data) => {
    if (
      user &&
      ((active !== MODERATORS && !hasStaffPrivilege(user)) ||
        (!document.hasFocus() && !hasStaffPrivilege(user)))
    ) {
      const { modMessageToasts } = user.userService.settings
      if (modMessageToasts) {
        toast(SplitButtons, {
          data: {
            title: 'Message from Moderator',
            content: data.message
          },
          autoClose: 10000,
          ariaLabel: `Message from Moderator: ${data.message}`,
          position: 'bottom-right',
          theme: 'dark',
          closeButton: false,
          hideProgressBar: false
        })
      }
      setModNotifCounter((modNotifCounter) => modNotifCounter + 1)
    }
  }

  function SplitButtons({ data, closeToast }) {
    const handleReply = () => {
      handleClick(MODERATORS)
      closeToast('reply')
    }

    return (
      <div className="grid grid-cols-[1fr_1px_80px] w-full">
        <div className="flex flex-col p-4">
          <h3 className="text-white text-sm font-semibold">{data.title}</h3>
          <p className="text-sm">{htmlTruncate(data.content, 50)}</p>
        </div>
        <div className="bg-zinc-900/20 h-full" />
        <div className="grid grid-rows-[1fr_1px_1fr] h-full">
          <button
            onClick={handleReply}
            className="text-[11px] font-black uppercase text-green-500 dark:text-green-500 tracking-wider"
            aria-label="Reply to moderator"
          >
            Reply
          </button>
          <div className="bg-zinc-900/20 w-full" />
          <button
            onClick={() => closeToast('ignore')}
            className="text-[11px] font-medium uppercase text-neutral-400 dark:text-neutral-600 tracking-wider"
            aria-label="Ignore moderator message"
          >
            Ignore
          </button>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (user) {
      setModNotifCounter(user.userService?.notifications?.privateMessages || 0)
      setTradeNotifCounter(0)
    }
  }, [user, user?.notifications])

  useEffect(() => {
    setPageTitleCounter(modNotifCounter + tradeNotifCounter)
  }, [modNotifCounter, tradeNotifCounter])

  useEffect(() => {
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
    }
  }, [active, tradesSocket, modNotifCounter, tradeNotifCounter])

  const handlePostTrade = (data) => {
    if (data.itemNumber === PRODUCT_CODE) {
      if (active !== TRADES || !document.hasFocus()) {
        setTradeNotifCounter((tradeNotifCounter) => tradeNotifCounter + 1)
      }
      const alertSound = new Audio(audio.trade)
      alertSound.volume = 0.5
      alertSound.play()
    }
  }

  const onFocus = () => {
    setPageTitleDefault()
    resetNotifCount(active)
  }

  const onBlur = () => {
    setPageTitleCounter(modNotifCounter + tradeNotifCounter)
  }

  const handleClick = (channel) => {
    handleSetChannel(channel)
    resetNotifCount(channel)
  }

  const resetNotifCount = async (type) => {
    let setting = ''
    let reset = false
    switch (type) {
      case MODERATORS: {
        setting = 'privateMessages'
        reset = modNotifCounter > 0
        const response = await putUser(
          user,
          'userService.notifications.privateMessages',
          0
        )
        if (response && response.status === 200) {
          updateUser(response.data)
        }
        break
      }
      case TRADES:
        setting = 'trades'
        reset = tradeNotifCounter > 0
        break
    }
  }

  return (
    <div className="h-auto flex-none ">
      <div className=" overflow-visible flex h-full w-full flex-col justify-between">
        <ul className="flex w-full gap-0 md:gap-2 border-y md:border-y-0 border-brand-primary-600 divide-x divide-brand-primary-600 md:divide-x-0">
          {publicChatEnabled && (
            <li className="w-full">
              <button
                onClick={() => handleClick(MEMBERS)}
                className={`chat-nav-button ${
                  active === MEMBERS
                    ? 'chat-nav-button-active'
                    : 'chat-nav-button-inactive'
                }`}
              >
                Chat
              </button>
            </li>
          )}
          {tradesEnabled && (
            <li className="w-full">
              <button
                onClick={() => handleClick(TRADES)}
                className={`chat-nav-button ${
                  active === TRADES
                    ? 'chat-nav-button-active'
                    : 'chat-nav-button-inactive'
                }`}
              >
                <span className="inline">Trades</span>
                <div className="inline">
                  {tradeNotifCounter > 0 && (
                    <Notification count={tradeNotifCounter} />
                  )}
                </div>
              </button>
            </li>
          )}
          {privateChatEnabled && (
            <li className="w-full">
              <button
                onClick={() => handleClick(MODERATORS)}
                className={`chat-nav-button ${
                  active === MODERATORS
                    ? 'chat-nav-button-active'
                    : 'chat-nav-button-inactive'
                }`}
              >
                Mods
                {modNotifCounter > 0 && (
                  <Notification count={modNotifCounter} />
                )}
              </button>
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}

ChatSectionNav.propTypes = {
  active: PropTypes.string.isRequired,
  handleSetChannel: PropTypes.func.isRequired
}

const Notification = ({ count }) => {
  return (
    <div
      className={`absolute mt-0.5 md:mt-1 lg:mt-0.5 ml-1 md:ml-0.5 lg:ml-1 inline-flex items-center justify-center rounded-full bg-yellow-500 h-4 md:h-3 lg:h-4 ${
        count > 9 ? 'w-6 md:w-4 lg:w-6' : 'w-4 md:w-3 lg:w-4'
      }`}
    >
      <span className="font-bold text-xs md:text-[9px] lg:text-xs text-black tabular-nums">
        {count > 9 ? '9+' : count}
      </span>
    </div>
  )
}

Notification.propTypes = {
  count: PropTypes.number.isRequired
}

export default ChatSectionNav
